import IconButton from '@atlaskit/button/icon/button';

import Heading from '@atlaskit/heading/heading';

import CrossIcon from '@atlaskit/icon/core/cross';

import { AtlassianIcon } from '@atlaskit/logo/atlassian-icon';

import { useModal } from '@atlaskit/modal-dialog/hooks';

import { Box } from '@atlaskit/primitives/box';

import { Flex } from '@atlaskit/primitives/components/flex';

import { Grid } from '@atlaskit/primitives/grid';

import { Stack } from '@atlaskit/primitives/stack';

import { Text } from '@atlaskit/primitives/text';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { getContentConfig, getGotItButton } from './ModalVariants';

import { SkeletonImage } from './SkeletonImage';

import { TwcModalButton } from './TwcModalButton';

import { buttonAndAltMessages, iconLabelMessages } from './messages';

import TwcIllustrationDark from '../../assets/TWC-Illustration-Dark.png';

import TwcIllustrationLight from '../../assets/TWC-Illustration-Light.png';

import { type Button as ButtonType, type Variant } from '../types';

import { type JSX } from 'react/jsx-runtime';

// width of modal when window width is 768px
const MOBILE_MODE_BREAKPOINT = 628;

// width of modal when window width is 1024px
const MEDIUM_BREAKPOINT = 884;

const modalContainerStyles = xcss({
	position: 'relative',
	overflow: 'hidden',
});

const closeButtonStyles = xcss({
	position: 'absolute',
	top: 'space.300',
	right: 'space.300',
	zIndex: 'modal',
});

const featureListStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const contentContainerStyles = xcss({
	padding: 'space.600',
});

const imageStyles = {
	width: '100%',
	height: '100%',
	objectFit: 'cover' as const,
	borderTopRightRadius: '4px',
	borderBottomRightRadius: '4px',
};

export type TwcOnboardingModalProps = {
	onStartTour: () => void;
	onDismiss: (interaction?: 'closed' | 'acknowledged' | 'ctaActioned') => void;
	variant: Exclude<Variant, 'none'>;
	showSpotlightTour: boolean;
	appOneName?: string;
	appTwoName?: string;
};

export const TwcOnboardingModalContent = ({
	onDismiss,
	onStartTour,
	variant,
	showSpotlightTour,
	appOneName,
	appTwoName,
}: TwcOnboardingModalProps): JSX.Element => {
	const { titleId } = useModal();
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const content = getContentConfig(variant, formatMessage, {
		showSpotlightTour,
		appOneName,
		appTwoName,
	});
	const [isMobileWidth, setIsMobileWidth] = useState<boolean>(false);
	const [isLessThanMediumWidth, setIsLessThanMediumWidth] = useState<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			const width = entries && entries[0]?.contentRect.width;
			if (width && width <= MOBILE_MODE_BREAKPOINT) {
				setIsMobileWidth(true);
				setIsLessThanMediumWidth(true);
			} else if (width && width < MEDIUM_BREAKPOINT) {
				setIsMobileWidth(false);
				setIsLessThanMediumWidth(true);
			} else {
				setIsLessThanMediumWidth(false);
				setIsMobileWidth(false);
			}
		});

		if (containerRef?.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	});

	const bold = (chunks: React.ReactNode) => <Text weight="bold">{chunks}</Text>;

	const handleStartTour = useCallback(
		() => () => {
			onStartTour();
		},
		[onStartTour],
	);

	const handleCloseButtonClick = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'closeModalButton',
			attributes: {
				variant,
			},
		});
		onDismiss();
	}, [onDismiss, fireAnalyticsEvent, variant]);

	const handleDismissAction = useCallback(
		(button: ButtonType) => () => {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: button.actionSubject,
				attributes: button.analyticAttributes,
			});
			onDismiss(button.id === 'got-it-button' ? 'acknowledged' : 'ctaActioned');
		},
		[onDismiss, fireAnalyticsEvent],
	);

	// Link action also dismisses the modal just like dismiss, leaving as a separate function for clarity, this behavior could be changed if needed.
	const handleLinkAction = useCallback(
		(button: ButtonType) => () => {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: button.actionSubject,
				attributes: button.analyticAttributes,
			});
			onDismiss('ctaActioned');
		},
		[onDismiss, fireAnalyticsEvent],
	);

	return (
		<Box xcss={modalContainerStyles} ref={containerRef}>
			<Box xcss={closeButtonStyles}>
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					testId="twc-onboarding-modal-close-button"
					label={formatMessage(buttonAndAltMessages.closeButtonLabel)}
					onClick={handleCloseButtonClick}
				/>
			</Box>
			<Grid
				templateColumns={isLessThanMediumWidth ? '1fr' : '1fr 1fr'}
				gap="space.0"
				templateRows="568px"
			>
				<Flex xcss={contentContainerStyles} direction="column" justifyContent="space-between">
					<Stack space="space.0">
						<Box paddingBlockEnd="space.500">
							<AtlassianIcon
								label={formatMessage(iconLabelMessages.atlassianLogoLabel)}
								appearance="brand"
								size="small"
							/>
						</Box>
						<Box paddingBlockEnd="space.150">
							<Heading as="h1" size="large" id={titleId}>
								<FormattedMessage {...content.title} />
							</Heading>
						</Box>
						<Box paddingBlockEnd="space.300">
							<Text as="p" color="color.text.subtle">
								<FormattedMessage {...content.description} />
							</Text>
						</Box>
						<Stack>
							{content.features.map((feature, index: number) => {
								return (
									<Flex key={index} alignItems="center" gap="space.150" xcss={featureListStyles}>
										{feature.icon}
										<Text as="p">
											<FormattedMessage
												{...feature.message}
												values={{
													bold,
													appOneName,
													appTwoName,
												}}
											/>
										</Text>
									</Flex>
								);
							})}
						</Stack>
					</Stack>
					<Box>
						<Flex justifyContent="end" gap="space.100">
							{isMobileWidth && showSpotlightTour ? (
								<TwcModalButton
									button={getGotItButton(variant)}
									onClickAction={() => handleDismissAction(getGotItButton(variant))}
								/>
							) : (
								<>
									{content.buttons.map((button) => {
										const onClickAction = () => {
											switch (button.action) {
												case 'link':
													return handleLinkAction(button);
												case 'dismiss':
													return handleDismissAction(button);
												case 'takeTour':
													return handleStartTour();
											}
										};
										return (
											<TwcModalButton
												key={button.id}
												button={button}
												onClickAction={onClickAction}
											/>
										);
									})}
								</>
							)}
						</Flex>
					</Box>
				</Flex>
				{!isLessThanMediumWidth && (
					<SkeletonImage
						src={TwcIllustrationLight}
						srcDark={TwcIllustrationDark}
						alt={formatMessage(buttonAndAltMessages.illustrationAlt)}
						imageStyles={imageStyles}
						testId="twc-onboarding-modal-image"
					/>
				)}
			</Grid>
		</Box>
	);
};
